/**
 * Settings panel — Odino agent (ADR-0024 / ADR-0071): WHICH LLM configuration the
 * build companion uses. Provider / model / key are authored in Configurazioni LLM;
 * this panel only binds one to Odino. Gated `odino:use`. Rendered as a panel inside
 * the unified Settings page — Odino's home is Impostazioni and nowhere else.
 */
import { useEffect, useState } from "react";
import { Bot } from "lucide-react";
import { useTranslation } from "react-i18next";

import { OdinoSettingsForm } from "@/components/odino/OdinoSettingsForm";
import { type OdinoSettings, odinoApi } from "@/lib/api-odino";

export function OdinoSection() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<OdinoSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    void odinoApi
      .getSettings()
      .then(setSettings)
      .catch(() => setError(t("settings.odino.loadError", "Impossibile caricare le impostazioni di Odino.")));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-foreground">{t("settings.odino.title", "Odino — agente")}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t(
            "settings.odino.subtitle",
            "Quale configurazione LLM usa Odino, il compagno di build. Provider, modello e chiave si gestiscono in Configurazioni LLM.",
          )}
        </p>
      </div>
      <div className="rounded-lg border border-border bg-surface p-5">
        {error ? (
          <div className="text-sm text-danger">
            {error}{" "}
            <button type="button" className="text-accent underline" onClick={load}>
              {t("common.retry", "Riprova")}
            </button>
          </div>
        ) : !settings ? (
          <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-2">
              <Bot className="h-5 w-5 text-accent" strokeWidth={1.5} />
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  settings.configured ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                }`}
              >
                {settings.configured
                  ? `${t("settings.odino.configured", "Configurato")} · ${settings.provider} · ${settings.model}`
                  : t("settings.odino.unconfigured", "Non configurato (Odino risponde in Echo)")}
              </span>
            </div>
            <OdinoSettingsForm settings={settings} onSaved={setSettings} />
          </>
        )}
      </div>
    </div>
  );
}
